import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { User, Briefcase, Landmark, ShieldCheck } from "lucide-react";
import { get } from "../../../services/lewooApi";
import { ProfileSkeleton } from "../../../components/admin/LoadingSkeleton";
import EmployeeAvatar from "../../../components/admin/EmployeeAvatar";

const EMPTY_PROFILE = {
  id: null,
  name: "—",
  jobTitle: "—",
  jobPosition: "—",
  department: "—",
  manager: "—",
  coach: "—",
  workEmail: "—",
  workPhone: "—",
  mobilePhone: "—",
  employeeType: "—",
  birthday: "—",
  gender: "—",
  nationality: "—",
  nationalId: "—",
  phone: "—",
  personalMobile: "—",
  privateEmail: "—",
  emergencyContact: "—",
  emergencyPhone: "—",
  homeAddress: "—",
  joiningDate: "—",
  employeeNumber: "—",
  officeId: "—",
  schedule: "—",
  contractId: "—",
  marital: "—",
  status: "—",
  categories: "—",
};

function val(v) {
  if (v === false || v === null || v === undefined) return "—";
  if (Array.isArray(v)) return v[1] || "—";
  return String(v);
}

const ESSProfilePage = () => {
  const intl = useIntl();
  const { user } = useSelector((state) => state.auth);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [profileEmp, setProfileEmp] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [empsRes, deptRes, jobsRes, partnersRes] = await Promise.all([
        get("/api/employees?limit=500"),
        get("/api/departments"),
        get("/api/jobs").catch(() => ({ data: [] })),
        get("/api/partners").catch(() => ({ data: [] })),
      ]);
      const allEmps = empsRes?.data || [];
      const deptRows = deptRes?.data || [];
      const deptMap = {};
      deptRows.forEach((d) => { deptMap[d.id] = d.name || d.complete_name || d.id; });
      const empNameMap = {};
      allEmps.forEach((e) => { empNameMap[e.id] = e.name || e.id; });
      const jobMap = {};
      (jobsRes?.data || []).forEach((j) => { jobMap[j.id] = j.name || j.id; });
      const partnerMap = {};
      (partnersRes?.data || []).forEach((p) => { partnerMap[p.id] = p.display_name || p.name || p.id; });

      const email = user?.email;
      const login = user?.login;
      const emp =
        allEmps.find((e) => email && (e.work_email === email || e.email === email)) ||
        allEmps.find((e) => login && (e.work_email === login || e.email === login)) ||
        allEmps.find((e) => user?.employee_id && (e.id === user.employee_id || e.employee_id === user.employee_id)) ||
        null;

      if (!emp) { setLoaded(true); return; }

      const rawCats = emp.category_ids;
      let categories = [];
      if (Array.isArray(rawCats)) {
        categories = rawCats
          .map((c) => (typeof c === "string" ? c : c?.name))
          .filter(Boolean);
      }

      setProfileEmp(emp);

      const genderLabel = emp.gender === "male"
        ? intl.formatMessage({ id: "ess.profile.genderMale" })
        : emp.gender === "female"
          ? intl.formatMessage({ id: "ess.profile.genderFemale" })
          : (emp.gender === "other" || emp.gender === "O" || emp.gender === "o")
            ? intl.formatMessage({ id: "ess.profile.genderOther" })
            : emp.gender || "—";

      const maritalLabel = emp.marital === "single"
        ? intl.formatMessage({ id: "ess.profile.maritalSingle" })
        : emp.marital === "married"
          ? intl.formatMessage({ id: "ess.profile.maritalMarried" })
          : emp.marital || "—";

      setProfile({
        id: emp.id,
        name: emp.name,
        jobTitle: val(emp.job_title),
        jobPosition: emp.job_id ? (jobMap[emp.job_id] || "—") : "—",
        department: emp.department_id ? (deptMap[emp.department_id] || "—") : "—",
        manager: emp.parent_id ? (empNameMap[emp.parent_id] || "—") : "—",
        coach: emp.coach_id ? (empNameMap[emp.coach_id] || "—") : "—",
        workEmail: val(emp.work_email),
        workPhone: val(emp.work_phone),
        mobilePhone: val(emp.mobile_phone),
        employeeType: emp.employee_type === "employee"
          ? intl.formatMessage({ id: "ess.profile.employeeType" })
          : emp.employee_type || "—",
        birthday: emp.birthday || "—",
        gender: genderLabel,
        nationality: val(emp.country_id),
        nationalId: val(emp.identification_id),
        phone: val(emp.phone),
        personalMobile: val(emp.personal_mobile),
        privateEmail: val(emp.private_email),
        emergencyContact: val(emp.emergency_contact),
        emergencyPhone: val(emp.emergency_phone),
        homeAddress: emp.address_home_id ? (partnerMap[emp.address_home_id] || "—") : "—",
        joiningDate: emp.joining_date || "—",
        employeeNumber: val(emp.employee_number),
        officeId: val(emp.office_id),
        schedule: val(emp.resource_calendar_id),
        contractId: val(emp.contract_id),
        marital: maritalLabel,
        status: emp.state === "draft"
          ? intl.formatMessage({ id: "ess.profile.statusActive" })
          : emp.state || intl.formatMessage({ id: "ess.profile.statusActive" }),
        categories: categories.length ? categories.join(", ") : "—",
      });
    } catch (_) {}
    setLoaded(true);
  }, [intl, user?.email, user?.login, user?.employee_id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const SECTIONS = profile ? [
    {
      id: "personal",
      title: intl.formatMessage({ id: "ess.profile.sectionPersonal" }),
      icon: User,
      fields: [
        { key: "name", label: intl.formatMessage({ id: "ess.profile.fullName" }) },
        { key: "birthday", label: intl.formatMessage({ id: "ess.profile.birthday" }) },
        { key: "gender", label: intl.formatMessage({ id: "ess.profile.gender" }) },
        { key: "marital", label: intl.formatMessage({ id: "ess.profile.marital" }) },
        { key: "nationality", label: intl.formatMessage({ id: "ess.profile.nationality" }) },
        { key: "nationalId", label: intl.formatMessage({ id: "ess.profile.nationalId" }) },
        { key: "phone", label: intl.formatMessage({ id: "ess.profile.phone" }) },
        { key: "personalMobile", label: intl.formatMessage({ id: "ess.profile.personalMobile" }) },
        { key: "privateEmail", label: intl.formatMessage({ id: "ess.profile.privateEmail" }) },
        { key: "emergencyContact", label: intl.formatMessage({ id: "ess.profile.emergencyContact" }) },
        { key: "emergencyPhone", label: intl.formatMessage({ id: "ess.profile.emergencyPhone" }) },
        { key: "homeAddress", label: intl.formatMessage({ id: "ess.profile.address" }) },
      ],
    },
    {
      id: "work",
      title: intl.formatMessage({ id: "ess.profile.sectionWork" }),
      icon: Briefcase,
      fields: [
        { key: "workEmail", label: intl.formatMessage({ id: "ess.profile.workEmail" }) },
        { key: "workPhone", label: intl.formatMessage({ id: "ess.profile.workPhone" }) },
        { key: "jobTitle", label: intl.formatMessage({ id: "ess.profile.jobTitle" }) },
        { key: "jobPosition", label: intl.formatMessage({ id: "ess.profile.position" }) },
        { key: "department", label: intl.formatMessage({ id: "ess.profile.department" }) },
        { key: "manager", label: intl.formatMessage({ id: "ess.profile.manager" }) },
        { key: "coach", label: intl.formatMessage({ id: "ess.profile.coach" }) },
        { key: "officeId", label: intl.formatMessage({ id: "ess.profile.office" }) },
        { key: "schedule", label: intl.formatMessage({ id: "ess.profile.schedule" }) },
        { key: "employeeType", label: intl.formatMessage({ id: "ess.profile.contractType" }) },
        { key: "joiningDate", label: intl.formatMessage({ id: "ess.profile.joiningDate" }) },
        { key: "employeeNumber", label: intl.formatMessage({ id: "ess.profile.employeeNumber" }) },
        { key: "categories", label: intl.formatMessage({ id: "ess.profile.tags" }) },
      ],
    },
    {
      id: "bank",
      title: intl.formatMessage({ id: "ess.profile.sectionBank" }),
      icon: Landmark,
      fields: [
        { key: "bank", label: intl.formatMessage({ id: "ess.profile.bank" }) },
        { key: "accountName", label: intl.formatMessage({ id: "ess.profile.accountName" }) },
        { key: "accountNumber", label: intl.formatMessage({ id: "ess.profile.accountNumber" }) },
      ],
    },
  ] : [];

  const QUICK_INFO = profile ? [
    { label: intl.formatMessage({ id: "ess.profile.qiEmployeeNumber" }), value: profile.employeeNumber },
    { label: intl.formatMessage({ id: "ess.profile.qiDepartment" }), value: profile.department },
    { label: intl.formatMessage({ id: "ess.profile.qiManager" }), value: profile.manager },
    { label: intl.formatMessage({ id: "ess.profile.qiJoining" }), value: profile.joiningDate },
    { label: intl.formatMessage({ id: "ess.profile.qiContract" }), value: profile.employeeType },
    { label: intl.formatMessage({ id: "ess.profile.qiOffice" }), value: profile.officeId },
  ] : [];

  if (!loaded) return <ProfileSkeleton />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {intl.formatMessage({ id: "ess.profile.title" })}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {intl.formatMessage({ id: "ess.profile.subtitle" })}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 text-center transition-all hover:shadow-md">
            <div className="w-20 h-20 mx-auto mb-3 overflow-hidden">
              <EmployeeAvatar employee={profileEmp} size={80} fontSize={30} />
            </div>
            <h3 className="text-base font-bold text-gray-900">{profile.name}</h3>
            <p className="text-sm text-gray-500 mt-1">{profile.jobTitle}</p>
            <div className="mt-3 flex justify-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                <ShieldCheck size={12} className="text-emerald-600" />
                {profile.status}
              </span>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 transition-all hover:shadow-md">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">
              {intl.formatMessage({ id: "ess.profile.quickInfo" })}
            </h3>
            {QUICK_INFO.map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
                <span className="text-sm font-medium text-gray-700 text-right truncate pl-4">{value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          {SECTIONS.map((section) => {
            const SectionIcon = section.icon;
            return (
              <div key={section.id} className="bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2.5 px-6 py-4 border-b border-gray-100">
                  {SectionIcon && <SectionIcon size={18} className="text-emerald-600" />}
                  <h3 className="text-base font-bold text-gray-900">{section.title}</h3>
                </div>
                <div className="px-6 py-2">
                  {(() => {
                    const rows = [];
                    const fields = section.fields;
                    for (let i = 0; i < fields.length; i += 2) {
                      rows.push(fields.slice(i, i + 2));
                    }
                    return rows.map((row, i) => (
                      <div key={i} className={`grid grid-cols-1 sm:grid-cols-2 gap-x-8 py-4 ${i < rows.length - 1 ? "border-b border-gray-100" : ""}`}>
                        {row.map((field) => (
                          <div key={field.key}>
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">{field.label}</p>
                            <p className="text-sm font-medium text-gray-900">{profile[field.key] || "—"}</p>
                          </div>
                        ))}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ESSProfilePage;
